import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GoHomeFill } from "react-icons/go";
import {
  MdFeedback,
  MdVideoLibrary,
  MdHistory,
  MdWatchLater,
  MdPlaylistPlay,
} from "react-icons/md";
import { BiSolidVideoPlus, BiSolidUserCircle } from "react-icons/bi";
import { RiFireFill } from "react-icons/ri";
import { HiMiniBars3 } from "react-icons/hi2";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../../api/auth";
import { logout } from "../../slices/authSlice";
import { toast } from "react-toastify";

function MenuNavbar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector((state) => state.auth.user);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const authChecked = useSelector((state) => state.auth.authChecked);

  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const accountDropdownRef = useRef(null);
  const sidebarRef = useRef(null);
  const sidebarButtonRef = useRef(null);

  const handleLogout = async () => {
    try {
      await logoutUser();
      dispatch(logout());
      localStorage.removeItem("userData");
      setAccountDropdownOpen(false);
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const checkLogin = () => {
    if (isLoggedIn) {
      navigate("/newvideo");
    } else {
      navigate("/login");
      toast.info("Login to access this feature");
    }
  };

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (
        accountDropdownOpen &&
        accountDropdownRef.current &&
        !accountDropdownRef.current.contains(event.target)
      ) {
        setAccountDropdownOpen(false);
      }
    };
    document.addEventListener("click", handleDocumentClick);
    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, [accountDropdownOpen]);

  useEffect(() => {
    const handleClickOutsideSidebar = (event) => {
      if (!sidebarOpen) return;
      const clickedInsideSidebar =
        sidebarRef.current && sidebarRef.current.contains(event.target);
      const clickedSidebarButton =
        sidebarButtonRef.current && sidebarButtonRef.current.contains(event.target);
      if (!clickedInsideSidebar && !clickedSidebarButton) {
        setSidebarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideSidebar);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideSidebar);
    };
  }, [sidebarOpen]);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const toggleAccountDropdown = () => {
    setAccountDropdownOpen((prev) => !prev);
  };

  return (
    <>
      {/* Top Glass Navbar */}
      <nav className="sticky top-0 z-50 w-full glass-panel bg-[#0b132b]/90 border-b border-slate-800/80">
        <div className="px-4 py-3 flex items-center justify-between">
          {/* Left Side: Sidebar Button & Brand Logo */}
          <div className="flex items-center gap-3">
            <button
              ref={sidebarButtonRef}
              className="p-2 text-slate-300 hover:text-white bg-slate-800/60 hover:bg-slate-700/80 rounded-xl transition duration-200"
              onClick={toggleSidebar}
              aria-label="Toggle Navigation"
            >
              <HiMiniBars3 className="h-5 w-5" />
            </button>

            <Link to="/" className="flex items-center gap-2.5 group">
              {/* W Favicon Badge matching exact browser tab icon */}
              <div className="w-9 h-9 rounded-xl bg-[#00C2D1] flex items-center justify-center font-black text-black text-lg select-none group-hover:scale-105 transition-transform duration-200 shadow-sm">
                <span className="font-black text-black text-lg leading-none select-none">
                  W
                </span>
              </div>
              <span className="text-xl font-black tracking-tight text-white group-hover:text-cyan-400 transition-colors">
                IDEO
              </span>
            </Link>
          </div>

          {/* Right Side: Upload & User Menu */}
          <div className="flex items-center gap-3">
            {/* Upload Video Button */}
            <button
              onClick={checkLogin}
              title="Upload Video"
              className="p-2 text-slate-300 hover:text-cyan-400 bg-slate-800/60 hover:bg-slate-700/80 rounded-xl transition duration-200 flex items-center gap-1.5"
            >
              <BiSolidVideoPlus className="w-5 h-5 text-cyan-400" />
              <span className="hidden sm:inline text-xs font-semibold">Create</span>
            </button>

            {/* Authentication */}
            {!authChecked ? null : isLoggedIn ? (
              <div ref={accountDropdownRef} className="relative">
                <button
                  onClick={toggleAccountDropdown}
                  className="flex items-center focus:outline-none"
                >
                  {!user?.profileImageUrl ? (
                    <BiSolidUserCircle className="w-8 h-8 text-slate-400 hover:text-white transition" />
                  ) : (
                    <img
                      src={user.profileImageUrl}
                      className="rounded-full border-2 border-cyan-500/50 hover:border-cyan-400 transition w-8 h-8 object-cover"
                      alt="Profile"
                    />
                  )}
                </button>

                {/* Account Dropdown */}
                {accountDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 glass-panel bg-[#111a2e]/95 border border-slate-700/80 rounded-2xl shadow-2xl p-2 z-50">
                    <div className="p-3 border-b border-slate-700/60">
                      <p className="text-sm font-semibold text-slate-100 truncate">
                        {user?.name}
                      </p>
                      <p className="text-xs text-slate-400 truncate mt-0.5">
                        {user?.email}
                      </p>
                    </div>

                    <div className="py-1">
                      <Link
                        to={`/channel/${user?.id}`}
                        onClick={() => setAccountDropdownOpen(false)}
                        className="flex items-center px-3 py-2 text-xs font-medium text-slate-300 hover:text-white hover:bg-slate-800/70 rounded-xl transition"
                      >
                        Your Channel
                      </Link>
                      <Link
                        to="/settings"
                        onClick={() => setAccountDropdownOpen(false)}
                        className="flex items-center px-3 py-2 text-xs font-medium text-slate-300 hover:text-white hover:bg-slate-800/70 rounded-xl transition"
                      >
                        Settings
                      </Link>
                    </div>

                    <div className="border-t border-slate-700/60 pt-1">
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-3 py-2 text-xs font-semibold text-rose-400 hover:bg-rose-500/10 rounded-xl transition"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="glow-button px-4 py-1.5 rounded-xl text-xs font-bold text-white transition"
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Backdrop Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-xs z-40 transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        className={`fixed top-0 left-0 z-50 w-64 h-screen pt-16 transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } glass-panel bg-[#0b132b]/95 border-r border-slate-800`}
        aria-label="Sidebar"
      >
        <div className="h-full px-3 py-4 overflow-y-auto">
          <ul className="space-y-1 font-medium">
            <li>
              <Link
                to="/"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-slate-200 hover:text-white hover:bg-slate-800/60 rounded-xl transition group"
              >
                <GoHomeFill className="w-5 h-5 text-cyan-400" />
                <span>Home</span>
              </Link>
            </li>

            <li>
              <Link
                to="/trending"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-slate-200 hover:text-white hover:bg-slate-800/60 rounded-xl transition group"
              >
                <RiFireFill className="w-5 h-5 text-amber-400" />
                <span>Trending</span>
              </Link>
            </li>

            <hr className="border-slate-800 my-2" />

            <li>
              <Link
                to="/yourvideos"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-slate-200 hover:text-white hover:bg-slate-800/60 rounded-xl transition group"
              >
                <MdVideoLibrary className="w-5 h-5 text-blue-400" />
                <span>Your Videos</span>
              </Link>
            </li>

            <li>
              <Link
                to="/history"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-slate-200 hover:text-white hover:bg-slate-800/60 rounded-xl transition group"
              >
                <MdHistory className="w-5 h-5 text-purple-400" />
                <span>History</span>
              </Link>
            </li>

            <li>
              <Link
                to="/watchlater"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-slate-200 hover:text-white hover:bg-slate-800/60 rounded-xl transition group"
              >
                <MdWatchLater className="w-5 h-5 text-emerald-400" />
                <span>Watch Later</span>
              </Link>
            </li>

            <li>
              <Link
                to="/playlists"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-slate-200 hover:text-white hover:bg-slate-800/60 rounded-xl transition group"
              >
                <MdPlaylistPlay className="w-5 h-5 text-pink-400" />
                <span>Playlists</span>
              </Link>
            </li>

            <hr className="border-slate-800 my-2" />

            <li>
              <Link
                to="/feedback"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-slate-200 hover:text-white hover:bg-slate-800/60 rounded-xl transition group"
              >
                <MdFeedback className="w-5 h-5 text-slate-400" />
                <span>Feedback</span>
              </Link>
            </li>
          </ul>
        </div>
      </aside>
    </>
  );
}

export default MenuNavbar;